import React, { useEffect, useState } from 'react'
import MainLayout from '../../components/MainLayout';
import { 
    Card, 
    Select, 
    Row, 
    Col, 
    Typography, 
    Space, 
    Alert,
    message,
    Tabs
} from 'antd';
import { 
    SettingOutlined, 
    KeyOutlined,
    InfoCircleOutlined,
    ApiOutlined,
    SafetyOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import { ApiConfig } from '../../services/ApiConfigs';
import { ApiServices } from '../../services/apiServices';
import StoreApiKeys from '../../components/StoreApiKeys';
import SignatureTester from '../../components/SignatureTester';

const { Title, Text, Paragraph } = Typography;

const ASSIGNMENT_MODE_KEY = 'Shipment Assignment Mode';

const assignmentModeOptions = [
    {
        value: 'admin_assign',
        label: 'Admin assigns driver',
        description: 'Admin selects and assigns a driver to each order/shipment. Driver receives the assignment directly.'
    },
    {
        value: 'driver_accept',
        label: 'Driver accepts shipment',
        description: 'Admin creates a shipment; drivers see available shipments and can accept them. You can switch to admin assign anytime to assign directly.'
    },
];

const Setting = () => {
    const [assignmentMode, setAssignmentMode] = useState('admin_assign');
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('general');

    useEffect(() => {
        getAssignmentMode();
    }, []);

    const getAssignmentMode = async () => {
        try {
            setLoading(true);
            const res = await ApiServices('get', ApiConfig.APP_CONFIG);
            const list = res?.data?.data || [];
            const row = list.find((r) => r.Key === ASSIGNMENT_MODE_KEY);
            if (row && (row.value === 'admin_assign' || row.value === 'driver_accept')) {
                setAssignmentMode(row.value);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const handleAssignmentModeChange = async (value) => {
        try {
            setLoading(true);
            setAssignmentMode(value);
            const body = { Key: ASSIGNMENT_MODE_KEY, value };
            const res = await ApiServices('post', ApiConfig.APP_CONFIG, body);
            if (res?.success || res?.status === 200) {
                message.success('Shipment assignment setting updated successfully');
            } else {
                message.error('Failed to update setting');
            }
        } catch (error) {
            console.log(error);
            message.error('Failed to update setting');
        } finally {
            setLoading(false);
        }
    };

    const selectedAssignmentOption = assignmentModeOptions.find((o) => o.value === assignmentMode);

    const tabItems = [
        {
            key: 'general',
            label: (
                <Space>
                    <SettingOutlined />
                    <span>General</span>
                </Space>
            ),
            children: (
                <div>
                    <Card style={{ marginBottom: 24 }} bordered={false}>
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            <div>
                                <Title level={4} style={{ marginBottom: 16 }}>
                                    <Space>
                                        <SafetyOutlined style={{ color: '#1890ff' }} />
                                        <span>Shipment assignment</span>
                                    </Space>
                                </Title>
                                <Paragraph style={{ marginBottom: 24, color: '#595959', fontSize: 14 }}>
                                    Choose how drivers are assigned to orders: either you assign drivers yourself, or drivers can accept available shipments.
                                </Paragraph>
                                <div style={{
                                    marginBottom: 16,
                                    padding: 20,
                                    background: '#fafafa',
                                    borderRadius: 8,
                                    border: '1px solid #f0f0f0'
                                }}>
                                    <Text strong style={{ display: 'block', marginBottom: 16, fontSize: 15 }}>
                                        Assignment mode
                                    </Text>
                                    <Select
                                        value={assignmentMode}
                                        onChange={handleAssignmentModeChange}
                                        style={{ width: '100%', minWidth: 300 }}
                                        placeholder="Select mode"
                                        size="large"
                                        loading={loading}
                                        optionLabelProp="label"
                                    >
                                        {assignmentModeOptions.map((s) => (
                                            <Select.Option key={s.value} value={s.value} label={s.label}>
                                                <div style={{ padding: '4px 0' }}>
                                                    <div style={{ fontWeight: 500, fontSize: 14, lineHeight: '22px', marginBottom: 4 }}>
                                                        {s.label}
                                                    </div>
                                                    <div style={{ fontSize: 13, color: '#595959', lineHeight: '20px', marginTop: 4 }}>
                                                        {s.description}
                                                    </div>
                                                </div>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                    {selectedAssignmentOption && (
                                        <div style={{ marginTop: 12 }}>
                                            <Text style={{ fontSize: 13, color: '#595959' }}>
                                                <InfoCircleOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                                                {selectedAssignmentOption.description}
                                            </Text>
                                        </div>
                                    )}
                                </div>
                                <Alert
                                    message="You can change this anytime"
                                    description="Switching to &quot;Admin assigns driver&quot; lets you assign drivers directly. Switching to &quot;Driver accepts shipment&quot; lets drivers see and accept available shipments."
                                    type="info"
                                    icon={<InfoCircleOutlined />}
                                    showIcon
                                    style={{ marginTop: 16 }}
                                />
                            </div>
                        </Space>
                    </Card>
                </div>
            ),
        },
        {
            key: 'api',
            label: (
                <Space>
                    <ApiOutlined />
                    <span>API Configuration</span>
                </Space>
            ),
            children: (
                <div>
                    <Row gutter={[24, 24]}>
                        <Col xs={24} lg={12}>
                            <StoreApiKeys />
                        </Col>
                        <Col xs={24} lg={12}>
                            <SignatureTester />
                        </Col>
                    </Row>
                </div>
            ),
        },
    ];

    return (
        <MainLayout>
            {/* Page Header */}
            <div style={{ 
                marginBottom: 32,
                paddingBottom: 24,
                borderBottom: '1px solid #f0f0f0'
            }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space align="center" size="middle">
                        <div style={{
                            width: 48,
                            height: 48,
                            borderRadius: 12,
                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                        }}>
                            <SettingOutlined style={{ fontSize: 24, color: '#ffffff' }} />
                        </div>
                        <div>
                            <Title level={2} style={{ margin: 0, lineHeight: 1.2 }}>
                                Settings
                            </Title>
                            <Paragraph style={{ margin: 0, fontSize: 14, color: '#595959' }}>
                                Manage your application preferences and API configurations
                            </Paragraph>
                        </div>
                    </Space>
                </Space>
            </div>

            {/* Settings Content */}
            <Card 
                bordered={false}
                style={{
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                }}
            >
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                    size="large"
                    style={{
                        minHeight: 400
                    }}
                />
            </Card>
        </MainLayout>
    )
}

export default Setting